/**
 * Complete the set: a merchant-configurable cross-sell widget on product
 * pages. Checking an add-on row expands its own option pickers; a single
 * combined Add to Cart submits the main product plus every checked, fully
 * configured add-on together in one /cart/add.js request.
 *
 * Deliberately does NOT touch product-form.js. When zero add-ons are
 * checked, this intercepts nothing and the theme's normal single-item add
 * flow runs exactly as before -- see onSubmit()'s early return.
 */
class CompleteTheSet extends HTMLElement {
  constructor() {
    super();
    this.productInfo = this.closest('product-info');
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll('.complete-the-set__item').forEach((item) => this.initItem(item));

    // Listens on `window`, not `this.productInfo` -- the Section Store cart
    // drawer app (`section-store--cart-block` / block-cart.js) attaches its
    // own capture-phase 'submit' listener on `document` and unconditionally
    // calls stopImmediatePropagation() there to run its own single-item add
    // flow. `document` sits above `product-info` in the DOM, so a listener
    // on `product-info` loses that race every time regardless of script load
    // order -- Section Store's handler always fires first and kills the
    // event before this widget ever sees it, silently dropping every checked
    // add-on (confirmed live: only the main product ends up in /cart.js).
    // `window` is the only node guaranteed to capture before `document` no
    // matter what, so attaching there wins unconditionally. The explicit
    // `this.productInfo.contains(event.target)` check below is required
    // because of this -- unlike a `product-info`-scoped listener, `window`
    // sees every submit on the page, not just this widget's own form.
    if (this.productInfo) {
      window.addEventListener('submit', this.onSubmit, { capture: true });
    }
  }

  disconnectedCallback() {
    if (this.productInfo) {
      window.removeEventListener('submit', this.onSubmit, { capture: true });
    }
  }

  static parseJSON(scriptEl, fallback) {
    if (!scriptEl) return fallback;
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (error) {
      console.error('complete-the-set: failed to parse', scriptEl, error);
      return fallback;
    }
  }

  initItem(item) {
    const checkbox = item.querySelector('[data-addon-toggle]');
    const panel = item.querySelector('[data-addon-panel]');
    const variants = CompleteTheSet.parseJSON(item.querySelector('[data-addon-variants]'), []);
    const priceMap = CompleteTheSet.parseJSON(item.querySelector('[data-addon-price-map]'), {});
    const imageMap = CompleteTheSet.parseJSON(item.querySelector('[data-addon-image-map]'), {});
    const variantIdInput = item.querySelector('[data-addon-variant-id]');
    const errorEl = item.querySelector('[data-addon-error]');
    const priceDefaultEl = item.querySelector('[data-price-default]');
    const priceResolvedEl = item.querySelector('[data-price-resolved]');
    const imageEl = item.querySelector('[data-addon-image]');
    const defaultImageSrc = imageEl ? imageEl.getAttribute('src') : '';
    const optionInputs = Array.from(item.querySelectorAll('[data-addon-option]'));

    const getSelectedOptions = () => {
      const optionCount = optionInputs.reduce(
        (max, el) => Math.max(max, parseInt(el.dataset.optionIndex, 10) + 1),
        0
      );
      const selected = new Array(optionCount).fill(null);
      optionInputs.forEach((el) => {
        if (el.checked) selected[parseInt(el.dataset.optionIndex, 10)] = el.value;
      });
      return selected;
    };

    const resolveVariant = () => {
      if (optionInputs.length === 0) return variants[0] || null;
      const selected = getSelectedOptions();
      if (selected.some((value) => value === null)) return null;
      return variants.find((variant) => variant.options.every((value, i) => value === selected[i])) || null;
    };

    const updateOptionAvailability = () => {
      if (optionInputs.length === 0) return;
      const selected = getSelectedOptions();
      optionInputs.forEach((el) => {
        const idx = parseInt(el.dataset.optionIndex, 10);
        const candidate = selected.slice();
        candidate[idx] = el.value;
        const isAvailable = variants.some(
          (variant) =>
            variant.available && variant.options.every((value, i) => candidate[i] === null || value === candidate[i])
        );
        el.classList.toggle('visually-disabled', !isAvailable);
        el.classList.toggle('disabled', !isAvailable);
      });
    };

    const setError = (message) => {
      if (!errorEl) return;
      if (message) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      } else {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
    };

    // Swaps between the default "from" price (rendered server-side via
    // snippets/price.liquid, showing Dawn's own from/compare-at logic across
    // ALL of this add-on's variants) and the exact resolved variant's own
    // price once a shopper has actually picked options -- built from
    // priceMap rather than re-rendering price.liquid per variant, since some
    // add-ons carry 150+ variants. DOM nodes + textContent only, never
    // innerHTML with a concatenated string.
    const setPriceDisplay = (variant) => {
      if (!priceDefaultEl || !priceResolvedEl) return;
      const entry = variant ? priceMap[String(variant.id)] : null;
      if (!entry) {
        priceResolvedEl.hidden = true;
        priceResolvedEl.textContent = '';
        priceDefaultEl.hidden = false;
        return;
      }
      priceResolvedEl.textContent = '';
      if (entry.compareAt) {
        const compareEl = document.createElement('s');
        compareEl.className = 'price-item price-item--regular';
        compareEl.textContent = entry.compareAt;
        priceResolvedEl.appendChild(compareEl);
        priceResolvedEl.appendChild(document.createTextNode(' '));
      }
      priceResolvedEl.appendChild(document.createTextNode(entry.price));
      priceResolvedEl.hidden = false;
      priceDefaultEl.hidden = true;
    };

    const setImage = (variant) => {
      if (!imageEl) return;
      const mapped = variant ? imageMap[String(variant.id)] : null;
      imageEl.src = mapped || defaultImageSrc;
    };

    const applyResolvedVariant = (updatePrice) => {
      const variant = resolveVariant();
      if (variant && variant.available) {
        variantIdInput.value = variant.id;
        item.classList.remove('complete-the-set__item--unavailable');
        setError(null);
        if (updatePrice) {
          setPriceDisplay(variant);
          setImage(variant);
        }
      } else {
        variantIdInput.value = '';
        item.classList.add('complete-the-set__item--unavailable');
        if (updatePrice) {
          setPriceDisplay(null);
          setImage(variant);
        }
        if (checkbox.checked) {
          const strings = window.variantStrings || {};
          setError(variant ? strings.soldOut || 'Sold out' : strings.unavailable || 'Unavailable');
        }
      }
    };

    const toggle = (expand) => {
      checkbox.checked = expand;
      if (panel) panel.hidden = !expand;
      if (expand) {
        updateOptionAvailability();
        applyResolvedVariant(false);
      } else {
        setError(null);
      }
    };

    const row = item.querySelector('.complete-the-set__row');
    row.addEventListener('click', (event) => {
      if (event.target === checkbox) return;
      event.preventDefault();
      toggle(!checkbox.checked);
    });

    checkbox.addEventListener('click', () => toggle(checkbox.checked));

    optionInputs.forEach((el) =>
      el.addEventListener('change', () => {
        const fieldset = el.closest('fieldset');
        const selectedValueEl = fieldset && fieldset.querySelector('[data-selected-value]');
        if (selectedValueEl) selectedValueEl.textContent = el.value;

        updateOptionAvailability();
        applyResolvedVariant(true);
      })
    );

    updateOptionAvailability();
    applyResolvedVariant(false);
  }

  getCheckedItems() {
    return Array.from(this.querySelectorAll('.complete-the-set__item')).filter((item) => {
      const checkbox = item.querySelector('[data-addon-toggle]');
      return checkbox && checkbox.checked;
    });
  }

  onSubmit(event) {
    // Now listening on `window` (see connectedCallback), which sees every
    // submit on the page -- bail out immediately for anything that isn't
    // this widget's own main product form.
    if (!this.productInfo.contains(event.target)) return;

    const checkedItems = this.getCheckedItems();
    if (checkedItems.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    const items = [];
    let hasError = false;

    const mainVariantInput = this.productInfo.querySelector('.product-variant-id');
    if (mainVariantInput && mainVariantInput.value) {
      items.push({ id: mainVariantInput.value, quantity: 1 });
    }

    checkedItems.forEach((item) => {
      const variantIdInput = item.querySelector('[data-addon-variant-id]');
      const errorEl = item.querySelector('[data-addon-error]');
      if (variantIdInput && variantIdInput.value) {
        items.push({ id: variantIdInput.value, quantity: 1 });
      } else {
        hasError = true;
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable';
        }
      }
    });

    if (hasError) return;

    const submitButton = this.productInfo.querySelector('.product-form__submit');
    if (submitButton) submitButton.setAttribute('aria-disabled', 'true');

    // Deliberately a plain /cart/add.js call -- no `sections`/`sections_url`,
    // and nothing here calls `renderContents()` on Dawn's own <cart-drawer>/
    // <cart-notification> the way product-form.js's default single-item flow
    // does. This store's actual cart UI is the Section Store app's own drawer
    // (`section-store--cart-block`), which watches cart/add.js network
    // activity itself and opens/updates independently of who made the call --
    // confirmed live, a bare /cart/add.js request with none of the above is
    // enough for it to open correctly with every item shown.
    //
    // An earlier version of this call *did* fetch `sections` and call
    // `cart.renderContents()` on Dawn's native cart-drawer, matching
    // product-form.js's own flow. That drawer is invisible on this site
    // (`display:none`, superseded by Section Store's), but renderContents()
    // still flipped it to `.active` and applied Dawn's own scroll-lock
    // class/inline-style to <body>/<html> as a side effect of opening. Since
    // the user only ever sees and closes Section Store's separate drawer,
    // Dawn's drawer -- and its scroll-lock -- never got closed, leaving the
    // whole page unscrollable after any Complete the Set add (confirmed live
    // by reproducing it, then confirming the lock never appears at all once
    // this call was removed). Nothing here needs that native drawer open in
    // the first place, so this doesn't touch it at all rather than also
    // closing it again afterward.
    fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Unknown error');
        }
      })
      .catch((error) => {
        console.error(error);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'complete-the-set',
            errors: error.message,
            message: error.message,
          });
        }
        const errorWrapper = this.productInfo.querySelector('.product-form__error-message-wrapper');
        const errorMessage = this.productInfo.querySelector('.product-form__error-message');
        if (errorWrapper && errorMessage) {
          errorMessage.textContent = error.message || (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart.';
          errorWrapper.hidden = false;
        }
      })
      .finally(() => {
        if (submitButton) submitButton.removeAttribute('aria-disabled');
      });
  }
}

if (!customElements.get('complete-the-set')) {
  customElements.define('complete-the-set', CompleteTheSet);
}
